"use client";

import { Button } from "@/components/ui/button";
import { Lock, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCheckout } from "@/hooks/use-checkout";
import { toast } from "sonner";
import { getEffectiveCredits, isPromotionEnabled, PREVIEW_CTA_PACK_ID, getPackPriceUsd } from "@/lib/payments/client";

interface GenerationControlsProps {
  onGenerate: () => void;
  isGenerating: boolean;
  canGenerate: boolean;
  creditBalance: number;
  hasPurchasedAnyPack?: boolean;
  isCompanyMember?: boolean;
  remainingFreePreviews?: number;
  onGenerateFreePreview?: () => void;
  hideButton?: boolean;
}

// Fixed credit cost per HD preview (all generations use 1K resolution)
export const CREDIT_COST = 1;

export function GenerationControls({
  onGenerate,
  isGenerating,
  canGenerate,
  creditBalance,
  hasPurchasedAnyPack = false,
  isCompanyMember = false,
  remainingFreePreviews = 0,
  onGenerateFreePreview,
  hideButton = false,
}: GenerationControlsProps) {
  const checkout = useCheckout();
  const hasEnoughCredits = creditBalance >= CREDIT_COST;
  const hasNoCredits = creditBalance < CREDIT_COST;
  const isPromoEnabled = isPromotionEnabled(PREVIEW_CTA_PACK_ID);
  const promoCredits = getEffectiveCredits(PREVIEW_CTA_PACK_ID);
  const ctaPackPrice = getPackPriceUsd(PREVIEW_CTA_PACK_ID);
  
  // Check if user is eligible for free preview
  const isEligibleForFreePreview = 
    !isCompanyMember && 
    remainingFreePreviews > 0 && 
    !hasPurchasedAnyPack && 
    hasNoCredits &&
    !!onGenerateFreePreview;

  // Show prominent value-focused CTA when user has very low/no credits
  if (hasNoCredits) {
    // If eligible for free preview, don't show promotional content
    // Button is shown either here (desktop) or in sticky footer (mobile)
    if (isEligibleForFreePreview) {
      // On mobile, button is in sticky footer, so return empty/minimal
      if (hideButton) {
        return <div />;
      }
      // On desktop, show the button
      return (
        <div className="space-y-4">
          <Button
            className="w-full bg-primary hover:bg-primary/90 text-white font-semibold"
            size="lg"
            onClick={(e) => {
              e.preventDefault();
              onGenerateFreePreview?.();
            }}
            disabled={!canGenerate || isGenerating}
          >
            {isGenerating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                Generating...
              </>
            ) : (
              <>
                <span className="sm:hidden">
                  Generate free preview
                  {remainingFreePreviews > 0 ? ` (${remainingFreePreviews} left)` : ""}
                </span>
                <span className="hidden sm:inline">
                  Generate free preview
                  {remainingFreePreviews > 0 ? ` (${remainingFreePreviews} left)` : ""}
                </span>
              </>
            )}
          </Button>
          <p className="text-[10px] text-center text-white/50 sm:hidden">
            Result will be watermarked
          </p>
        </div>
      );
    }

    // Otherwise show the full promotional paywall card
    return (
      <div className="space-y-4">
        {/* Value-focused paywall card with visual proof */}
        <div className="rounded-xl bg-gradient-to-br from-primary/10 via-violet-500/5 to-primary/10 border border-primary/20 p-4">
          {/* Limited Time Badge */}
          {isPromoEnabled && (
            <div className="mb-3">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 text-amber-400 text-xs font-semibold animate-pulse">
                <Sparkles className="w-3 h-3" />
                20% Bonus Previews
              </div>
            </div>
          )}
          
          {/* Title */}
          <h3 className="font-semibold text-lg text-white mb-1">
            Start Creating Wheel Previews
          </h3>
          
          {/* Description */}
          <p className="text-sm text-muted-foreground mb-4">
            {isPromoEnabled 
              ? `Limited: 20% bonus previews (${promoCredits} previews for $${ctaPackPrice.toFixed(2)})`
              : `Create ${promoCredits} high-resolution previews with your own cars and wheels`
            }
          </p>
          
          {/* Value points */}
          <div className="text-center mb-4">
            <p className="text-sm font-medium text-emerald-400">
              High-resolution previews • No watermark • Custom wheel uploads
            </p>
          </div>
          
          {/* CTA Button */}
          {!hideButton && (
            <>
              <Button
                className="w-full bg-primary hover:bg-primary/90 text-white font-semibold"
                size="lg"
                onClick={() => {
                  checkout.mutate(
                    { packId: PREVIEW_CTA_PACK_ID, source: "generation_controls" },
                    {
                      onError: (error) => {
                        console.error("Checkout error:", error);
                        toast.error(error instanceof Error ? error.message : "Failed to start checkout");
                      },
                    }
                  );
                }}
                disabled={checkout.isPending}
              >
                {checkout.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    Get {promoCredits} Previews – ${ctaPackPrice.toFixed(2)}
                  </>
                )}
              </Button>

              {/* Trust micro-copy */}
              <div className="flex items-center justify-center gap-1.5 mt-2 text-xs text-muted-foreground">
                <Lock className="w-3 h-3 shrink-0" />
                <span>No subscription · Previews never expire</span>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Credit Balance & Generate Button */}
      <div className="space-y-2">
        <div className="flex justify-between items-center text-sm">
          <span className="text-muted-foreground">Your balance:</span>
          <span className={cn(
            "font-medium flex items-center gap-1",
            !hasEnoughCredits && "text-amber-400"
          )}>
            <Sparkles className="w-4 h-4" />
            {creditBalance} previews
          </span>
        </div>

        {!hideButton && (
          <>
            {hasEnoughCredits ? (
              <Button
                className="w-full"
                size="lg"
                onClick={(e) => {
                  e.preventDefault();
                  onGenerate();
                }}
                disabled={!canGenerate || isGenerating}
              >
                {isGenerating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                    Generating...
                  </>
                ) : (
                  <>Generate Preview</>
                )}
              </Button>
            ) : (
              <div className="space-y-2">
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <p className="text-sm text-amber-200 text-center">
                    Top up to generate HD previews
                  </p>
                </div>
                <Button
                  variant="default"
                  className="w-full bg-primary hover:bg-primary/90"
                  onClick={() => {
                    checkout.mutate(
                      { packId: PREVIEW_CTA_PACK_ID, source: "generation_controls" },
                      {
                        onError: (error) => {
                          console.error("Checkout error:", error);
                          toast.error(error instanceof Error ? error.message : "Failed to start checkout");
                        },
                      }
                    );
                  }}
                  disabled={checkout.isPending}
                >
                  {checkout.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate HD Images – ${ctaPackPrice.toFixed(2)}
                    </>
                  )}
                </Button>
                <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1.5">
                  <Lock className="w-3 h-3" />
                  No subscription · Previews never expire
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
